import { Typography } from '@mui/material';
import { MouseEvent, default as React, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Confirm from '../../common/components/Confirm';
import {
    createGroupByOptions,
    getReportSortFormConfig
} from '../../common/components/forms/groupByConfigurations/groupByUtils';
import {
    type Entity,
    getCustomSortFieldConfigMap,
    getCustomSortFieldConfigs,
    getCustomSortRankOrderFields
} from '../../common/constants/customSortFieldConfigs';
import { ENTITY_CONSTANTS, ENTITY_REFERENCE_LABEL } from '../../common/constants/entityConstants';
import { FORM_WIDGET_COMPONENTS } from '../../common/form/formConstants';
import { FormContainer } from '../../common/form/FormContainer';
import { FormContext } from '../../common/form/FormContext';
import FormItem from '../../common/form/FormItem';
import FormWidget from '../../common/form/FormWidget';
import { GroupOption } from '../../common/generateReport/reportTypes';
import { areSelectionsEqual, getOptionId, hasRankOrderValues } from '../../common/generateReport/reportUtils';
import I18n, { useI18n } from '../../common/i18n/I18n';
import OrganizationOptionRenderer from '../../organization/OrganizationOptionRenderer';
import { CUSTOM_SORT_KEY } from '../productionConstants';
import ProductionPersonOptionRendered, { getProductionPersonOption } from './production/ProductionPersonOptionRendered';

export type SortConfigFormItemProps = {
    titleToken?: string;
    entity: Entity;
};

type PendingRemovalState = {
    rankField: string;
    nextSelection: GroupOption[];
};

const EMPTY_GROUP_OPTIONS: GroupOption[] = [];

export default function SortConfigFormItems({ titleToken, entity }: SortConfigFormItemProps) {
    const t = useI18n();
    const entityLabel = `${entity.charAt(0).toUpperCase()}${entity.slice(1)}`;
    const { state, setState } = useContext(FormContext);
    const [pendingRemoval, setPendingRemoval] = useState<PendingRemovalState | null>(null);
    const [customRemovalPending, setCustomRemovalPending] = useState<boolean>(false);
    const sortFormConfig = useMemo(() => getReportSortFormConfig(entity), [entity]);
    const rankFieldConfigs = useMemo(() => getCustomSortFieldConfigMap(entity), [entity]);
    const rankFieldConfigList = useMemo(() => getCustomSortFieldConfigs(entity), [entity]);
    const rankOrderFields = useMemo(() => getCustomSortRankOrderFields(entity), [entity]);
    const groupByKeys = useMemo(() => sortFormConfig.resolveGroupByKeys(entity), [entity, sortFormConfig]);

    const sortByOptions = createGroupByOptions(sortFormConfig.sortByKeys, t);
    const keyWorkspaceGroupByOptions = createGroupByOptions(groupByKeys, t);
    const cohortSortByOptions = createGroupByOptions(sortFormConfig.cohortSortByKeys, t);

    const sortWorkspaceByValue = state?.[sortFormConfig.sortByFieldName];
    const isCustomSort = getOptionId(sortWorkspaceByValue) === CUSTOM_SORT_KEY;

    const groupByValue: GroupOption[] = state?.[sortFormConfig.groupByFieldName] || EMPTY_GROUP_OPTIONS;

    const groupByIds = useMemo(
        () =>
            (groupByValue || [])
                .map((option) => getOptionId(option))
                .filter((optionId): optionId is string => Boolean(optionId)),
        [groupByValue]
    );

    const selectedRankFieldConfigs = useMemo(
        () => groupByIds.map((groupId) => rankFieldConfigs[groupId]).filter(Boolean),
        [groupByIds, rankFieldConfigs]
    );

    const hasVisibleRankFields =
        (isCustomSort || (!isCustomSort && customRemovalPending)) && selectedRankFieldConfigs.length > 0;

    const hasAnyRankFieldSelection = selectedRankFieldConfigs.some(
        (config) => Array.isArray(state?.[config.name]) && state[config.name].length > 0
    );

    const previousGroupByRef = useRef<GroupOption[]>(state?.[sortFormConfig.groupByFieldName] || []);

    const previousIsCustomSortRef = useRef<boolean>(isCustomSort);

    useEffect(() => {
        const wasCustomSort = previousIsCustomSortRef.current;
        previousIsCustomSortRef.current = isCustomSort;
        if (wasCustomSort && !isCustomSort && groupByValue?.length) {
            setCustomRemovalPending(true);
        }
    }, [isCustomSort, groupByValue]);

    useEffect(() => {
        if (!isCustomSort) {
            return;
        }

        const previousSelection = previousGroupByRef.current || [];
        const currentSelection = groupByValue || [];

        if (areSelectionsEqual(previousSelection, currentSelection)) {
            return;
        }

        const removedOption = previousSelection.find((option) => {
            const optionId = getOptionId(option);
            return optionId && !currentSelection.some((nextOption) => getOptionId(nextOption) === optionId);
        });

        const optionId = removedOption && getOptionId(removedOption);
        const rankField = optionId && rankOrderFields[optionId];

        if (!rankField) {
            previousGroupByRef.current = currentSelection;
            return;
        }

        const rankFieldHasValues = hasRankOrderValues(state?.[rankField]);

        if (rankFieldHasValues) {
            setPendingRemoval({ rankField, nextSelection: currentSelection });
            setState((prev) => ({
                ...prev,
                [sortFormConfig.groupByFieldName]: previousSelection
            }));
            previousGroupByRef.current = previousSelection;
            return;
        }

        setState((prev) => ({
            ...prev,
            [sortFormConfig.groupByFieldName]: currentSelection,
            [rankField]: []
        }));
        previousGroupByRef.current = currentSelection;
    }, [groupByValue, isCustomSort, setState, rankOrderFields, sortFormConfig.groupByFieldName, state]);

    const handleConfirmRemoval = (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        if (!pendingRemoval) {
            return;
        }
        setState((prev) => ({
            ...prev,
            [sortFormConfig.groupByFieldName]: pendingRemoval.nextSelection,
            [pendingRemoval.rankField]: []
        }));
        previousGroupByRef.current = pendingRemoval.nextSelection || [];
        setPendingRemoval(null);
    };

    const handleCancelRemoval = (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        setPendingRemoval(null);
    };

    const handleConfirmCustomRemoval = (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        setState((prev) => {
            const nextState = { ...prev };
            delete nextState[sortFormConfig.groupByFieldName];
            rankFieldConfigList.forEach((config) => {
                delete nextState[config.name];
            });
            delete nextState[sortFormConfig.cohortSortByFieldName];
            return nextState;
        });
        previousGroupByRef.current = [];
        setCustomRemovalPending(false);
    };

    const handleCancelCustomRemoval = (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        setState((prev) => ({
            ...prev,
            [sortFormConfig.sortByFieldName]: CUSTOM_SORT_KEY
        }));
        setCustomRemovalPending(false);
    };

    const constructCustomOrderProps = (config) => {
        if (!config.isEntity) {
            return {
                component: FORM_WIDGET_COMPONENTS.metadataAutocomplete,
                metadataName: config.metadataName,
                ...(config.sortBy !== undefined && {
                    sortBy: config.sortBy,
                    optionsSort: false
                }),
                draggableChips: true
            };
        }

        const entityProps = {
            component: FORM_WIDGET_COMPONENTS.entityAutocomplete,
            entity: config.metadataName,
            preload: false,
            entityReferenceKey:
                config.metadataName === ENTITY_CONSTANTS.person
                    ? ENTITY_REFERENCE_LABEL.personReference
                    : ENTITY_REFERENCE_LABEL.reference,
            draggableChips: true
        };

        if (config.metadataName === ENTITY_CONSTANTS.person) {
            return {
                ...entityProps,
                OptionRenderer: (val) => <ProductionPersonOptionRendered option={val?.option} />,
                getOption: getProductionPersonOption
            };
        }

        if (config.metadataName === ENTITY_CONSTANTS.organization) {
            return {
                ...entityProps,
                OptionRenderer: OrganizationOptionRenderer,
                showInactive: true
            };
        }

        return entityProps;
    };

    return (
        <FormContainer titleI18nToken={titleToken}>
            <FormItem>
                <FormWidget
                    name={sortFormConfig.sortByFieldName}
                    label={<I18n token={sortFormConfig.sortByLabelToken} values={{ entity: entityLabel }} />}
                    component={FORM_WIDGET_COMPONENTS.autocomplete}
                    options={sortByOptions}
                />
            </FormItem>

            <FormItem hidden={!isCustomSort && !customRemovalPending}>
                <FormWidget
                    name={sortFormConfig.groupByFieldName}
                    label={<I18n token={sortFormConfig.groupByLabelToken} />}
                    component={FORM_WIDGET_COMPONENTS.autocomplete}
                    options={keyWorkspaceGroupByOptions}
                    multiple
                    formHelperText={
                        sortFormConfig.groupByHelperTextToken ? (
                            <I18n token={sortFormConfig.groupByHelperTextToken} />
                        ) : undefined
                    }
                />
            </FormItem>

            <>
                {selectedRankFieldConfigs.map((config) => (
                    <FormItem key={config.groupId} hidden={!hasVisibleRankFields}>
                        <FormWidget
                            name={config.name}
                            label={<I18n token={config.labelToken} />}
                            {...constructCustomOrderProps(config)}
                            multiple
                            filterSelectedOptions
                        />
                    </FormItem>
                ))}
            </>

            <FormItem>
                <FormWidget
                    hidden={
                        (!customRemovalPending && selectedRankFieldConfigs.length === 0) || !hasAnyRankFieldSelection
                    }
                    name={sortFormConfig.cohortSortByFieldName}
                    label={<I18n token={sortFormConfig.cohortSortByLabelToken} values={{ entity: entityLabel }} />}
                    component={FORM_WIDGET_COMPONENTS.autocomplete}
                    options={cohortSortByOptions}
                />
            </FormItem>

            {pendingRemoval && (
                <Confirm
                    open
                    title={<I18n token="report.groupBy.rankOrder.confirmation.title" />}
                    body={
                        <Typography>
                            <I18n token="report.groupBy.rankOrder.confirmation.body" />
                        </Typography>
                    }
                    okLabelI18n="common.confirm"
                    onConfirm={handleConfirmRemoval}
                    onCancel={handleCancelRemoval}
                    isOkButtonContained
                />
            )}
            {customRemovalPending && (
                <Confirm
                    open
                    title={<I18n token="common.confirm.change" />}
                    body={
                        <Typography>
                            <I18n token="report.groupBy.change.confirmation.body" />
                        </Typography>
                    }
                    okLabelI18n="common.confirm"
                    onConfirm={handleConfirmCustomRemoval}
                    onCancel={handleCancelCustomRemoval}
                    isOkButtonContained
                />
            )}
        </FormContainer>
    );
}
